import React from 'react';
import useReport from '../lib/useReport';
import { money, moneyCompact, int, niceDate } from '../lib/format';
import { Card, KpiCard, Spinner, ErrorBox, Empty, DataTable, Delta } from '../components/ui';

// Daily Digest — "how did we do yesterday?" in one glance: the latest complete
// business day per store vs last week and the same weekday last year, with an
// exceptions-first layout. Performance only — fraud alerting is SD-Live's job.
// (This same payload becomes the morning email once SMTP is configured.)
const SEV = {
  bad: { icon: '🔴', color: 'var(--bad)' },
  warn: { icon: '🟠', color: '#b45309' },
  good: { icon: '🟢', color: 'var(--good)' },
};

export default function DigestPage() {
  // Deliberately ignores the global date filter: the digest is always
  // "the latest business day". Location scope still applies server-side.
  const { data, loading, error } = useReport('digest', {});

  if (error) return <ErrorBox message={error} />;
  if (loading || !data) return <Spinner label="Preparing the digest…" />;
  if (!data.digestDate) return <Empty>No sales data available yet.</Empty>;

  const { digestDate, weekday, summary: s, exceptions, stores, pipeline } = data;
  const comp = s.compLyNet > 0 ? (s.compNet - s.compLyNet) / s.compLyNet : null;

  return (
    <div className="grid" style={{ gap: 14 }}>
      {(pipeline.stale || pipeline.loadErrors.length > 0) && (
        <div className="error-box">
          {pipeline.stale && (
            <div><strong>⚠ Data is {pipeline.staleDays} days old</strong> — latest snapshot is {pipeline.snapshot} (loaded {niceDate(pipeline.dataThrough)}).</div>
          )}
          {pipeline.loadErrors.length > 0 && (
            <div style={{ marginTop: pipeline.stale ? 6 : 0 }}>
              {pipeline.loadErrors.length} failed snapshot load{pipeline.loadErrors.length > 1 ? 's' : ''} in the last 7 days
              {' '}(latest: {pipeline.loadErrors[0].dbName} — “{pipeline.loadErrors[0].log}”). The dashboard is serving the last good snapshot.
            </div>
          )}
        </div>
      )}

      <div className="grid kpi-grid">
        <KpiCard
          label={`Net Sales — ${weekday} ${niceDate(digestDate)}`}
          value={moneyCompact(s.netSales)}
          sub={`${s.storeCount} stores`}
        />
        <KpiCard label="Orders" value={int(s.orders)} sub={`AOV ${money(s.avgOrderValue)}`} />
        <KpiCard
          label="vs Last Week"
          value={s.lastWeekNet > 0 ? <Delta curr={s.netSales} prev={s.lastWeekNet} /> : '—'}
          sub={`${moneyCompact(s.lastWeekNet)} last ${weekday}`}
        />
        <KpiCard
          label="vs Last Year"
          value={comp === null ? '—' : `${comp > 0 ? '+' : ''}${(comp * 100).toFixed(1)}%`}
          curr={s.compNet} prev={s.compLyNet}
          sub={`same ${weekday}, ${s.compBase} comp stores`}
        />
      </div>

      <Card
        tour="digest-exceptions"
        title="Exceptions"
        sub={`stores outside their normal range for a ${weekday} — 4-week same-weekday baseline`}
      >
        {exceptions.length === 0 ? (
          <Empty>All stores within normal ranges. 🎉</Empty>
        ) : (
          <div>
            {exceptions.map((e, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '7px 4px', borderBottom: '1px solid var(--grid)', fontSize: 13.5 }}>
                <span>{SEV[e.severity]?.icon || '•'}</span>
                <strong style={{ whiteSpace: 'nowrap' }}>{e.name}</strong>
                <span style={{ color: 'var(--ink-2)' }}>{e.message}</span>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card tour="digest-stores" title="By Store" sub={`net sales for ${niceDate(digestDate)} vs last ${weekday} and the same ${weekday} last year`}>
        <DataTable
          csvName={`digest-${digestDate}.csv`}
          defaultSort={{ key: 'netSales', dir: 'desc' }}
          columns={[
            { key: 'name', label: 'Store' },
            { key: 'netSales', label: 'Net Sales', num: true, render: r => money(r.netSales), csv: r => (r.netSales / 100).toFixed(2), footer: rows => money(rows.reduce((a, r) => a + r.netSales, 0)) },
            { key: 'orders', label: 'Orders', num: true, render: r => int(r.orders), csv: r => r.orders, footer: rows => int(rows.reduce((a, r) => a + r.orders, 0)) },
            { key: 'avgOrderValue', label: 'AOV', num: true, render: r => r.orders ? money(r.avgOrderValue) : '—', csv: r => (r.avgOrderValue / 100).toFixed(2) },
            {
              key: 'vsLastWeek', label: 'vs Last Week', num: true,
              sortValue: r => (r.lastWeekNet > 0 ? (r.netSales - r.lastWeekNet) / r.lastWeekNet : -Infinity),
              render: r => r.lastWeekNet > 0 ? <Delta curr={r.netSales} prev={r.lastWeekNet} /> : '—',
              csv: r => r.lastWeekNet > 0 ? (((r.netSales - r.lastWeekNet) / r.lastWeekNet) * 100).toFixed(1) : '',
            },
            {
              key: 'vsLastYear', label: 'vs LY', num: true,
              sortValue: r => (r.lastYearNet > 0 ? (r.netSales - r.lastYearNet) / r.lastYearNet : -Infinity),
              render: r => r.lastYearNet > 0 ? <Delta curr={r.netSales} prev={r.lastYearNet} /> : '—',
              csv: r => r.lastYearNet > 0 ? (((r.netSales - r.lastYearNet) / r.lastYearNet) * 100).toFixed(1) : '',
            },
            {
              key: 'refundAmount', label: 'Refunds', num: true,
              render: r => r.refundAmount ? money(r.refundAmount) : '—',
              csv: r => (r.refundAmount / 100).toFixed(2),
            },
          ]}
          rows={stores.map(r => ({ ...r, __key: r.locationId }))}
          footer
        />
        <div className="legend-note">
          Data through the {pipeline.snapshot} snapshot. “—” means the store has no comparable day (new store, or closed that day).
        </div>
      </Card>
    </div>
  );
}
